import styled from "styled-components/macro";
import { theme } from "ui/theme";
import PolicyLayout, {
  $PageTitle,
  $TextContainer,
  $OverviewText,
  $SectionTitle,
  $Paragraph,
  $UnorderedList,
  $ListItem,
  $ExternalLink,
  $PrivacyPolicyLink,
  $UserAgreementLink,
  $ReportAtAlongEmailLink,
  $SupportAtAlongEmailLink,
} from "templates/PolicyLayout";

import OpenBook from "images/open-book.png";
import OpenBook2x from "images/open-book@2x.png";
import CircleSun from "images/circle-sun.png";

const $PageTitleTextDecorator = styled.span`
  position: relative;

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: -50%;
    left: -5%;
    width: 110%;
    height: 160%;
    background-image: url(${CircleSun});
    background-repeat: no-repeat;
    background-position: bottom center;
    background-size: 100%;
    z-index: ${theme.zIndexBelow};

    @media screen and (${theme.BreakpointTabletDown}) {
      display: none;
    }
  }
`;

const PageTitle = () => {
  return (
    <$PageTitle>
      <$PageTitleTextDecorator>Code of Conduct</$PageTitleTextDecorator>
    </$PageTitle>
  );
};

export default function CodeOfConduct() {
  return (
    <PolicyLayout
      pageTitle={<PageTitle />}
      lastUpdatedDate="October 13, 2020"
      src={OpenBook}
      srcSet={OpenBook2x}
      alt="a graphic illustration of an open book with one page highlighted in yellow"
    >
      <$TextContainer>
        <$OverviewText size="h3">
          <$Paragraph>
            We are committed to making Along a safe space for students and
            teachers. Your well-being is our top priority, and everyone has a
            role to play. We look forward to working together with school
            administrators, teachers, students, and families to maintain the
            integrity of Along, and to ensure Along helps support student growth
            and learning. As such, every person using Along must adhere to the
            following guidelines that are designed to preserve and protect the
            psychological and physical safety of our community.
          </$Paragraph>
          <$Paragraph>
            We strongly encourage all users to report inappropriate content or
            use of Along to your school leader, or to contact our team directly
            for violations by emailing&nbsp;
            <$ReportAtAlongEmailLink />.
          </$Paragraph>
          <$Paragraph>
            Please note that your school’s policies also apply to your use of
            Along.
          </$Paragraph>
        </$OverviewText>

        <$SectionTitle size="h1">For Students</$SectionTitle>

        <$Paragraph>
          We want Along to be a safe space. If you have been bullied or
          harassed, are experiencing desires for self harm, or if you think
          someone you know may harm you, themselves, or someone else, please
          talk to a trusted adult and use these resources:
        </$Paragraph>
        <$UnorderedList>
          <$ListItem>
            <$ExternalLink
              href="https://suicidepreventionlifeline.org/"
              linkText="National Suicide Prevention Lifeline"
            />
          </$ListItem>
          <$ListItem>
            <$ExternalLink
              href="https://www.pacerkidsagainstbullying.org/#/home"
              linkText="Kids Against Bullying"
            />
            &nbsp;(for younger kids)
          </$ListItem>
          <$ListItem>
            <$ExternalLink
              href="https://www.stopbullying.gov/"
              linkText="StopBullying.gov"
            />
          </$ListItem>
          <$ListItem>
            <$ExternalLink
              href="https://www.crisistextline.org/"
              linkText="Crisis Text Link"
            />
            &nbsp;(text HOME to 741741 anytime to connect with a crisis
            counselor)
          </$ListItem>
        </$UnorderedList>

        <$SectionTitle size="h1">For all Along Users</$SectionTitle>

        <$UnorderedList>
          <$ListItem>
            Do not share content likely to be viewed as highly offensive or
            inappropriate for a school environment (such as pornography,
            depictions of graphic violence, glorification of violence, or
            promotion of drug use).
          </$ListItem>
          <$ListItem>
            Do not bully or harass other users. Communication on Along must be
            respectful.
          </$ListItem>
          <$ListItem>
            Do not attack or insult people based on characteristics like race,
            ethnicity, national origin, immigration status, class, religion,
            sexual preference or orientation, gender, gender identity, or
            disability. We do not permit hate speech.
          </$ListItem>
          <$ListItem>
            Do not share confidential or sensitive information of or about
            another person without their permission. Be careful about sharing
            your own personal information with others. Respect the privacy of
            others.
          </$ListItem>
          <$ListItem>
            Do not use Along in a way that threatens or risks the safety of
            another person. Never encourage another person to harm themselves.
          </$ListItem>
          <$ListItem>
            Do not use Along for any illegal purposes. Always comply with the
            law and the policies of your school when using the product.
          </$ListItem>
          <$ListItem>
            Do not log in as, or otherwise impersonate, another person. Do not
            allow another person to use your account.
          </$ListItem>
          <$ListItem>
            Do not share content that violates copyrights or other intellectual
            property rights or proprietary rights, such as pirated music or
            videos.
          </$ListItem>
          <$ListItem>
            Do not disable or intentionally bypass any security feature, access
            controls, or usage limits of Along.
          </$ListItem>
          <$ListItem>
            Do not share anything that contains software viruses, worms, or any
            other harmful code or interferes with the operation of, or places an
            unreasonable load on, Along (e.g. a denial of service attack).
          </$ListItem>
          <$ListItem>
            Do not use Along to interact with children in a way that may exploit
            or abuse them. This includes any content that presents children in a
            sexual manner, including any incitement for the creation of such
            content.
          </$ListItem>
          <$ListItem>
            Use Along only for educational purposes that help advance students’
            well-being and learning.
          </$ListItem>
          <$ListItem>
            Do not use Along for any commercial purposes. Do not send spam or
            other promotional content using Along. Do not rent, lease, loan,
            trade, sell/resell, or otherwise monetize Along or access to it.
          </$ListItem>
        </$UnorderedList>
        <$Paragraph>
          To keep Along a safe space for students and teachers, any content or
          behavior that goes against this Code of Conduct may result in accounts
          being deactivated, closed, or reported to the appropriate authorities.
          In addition, your school leader will have the authority to view a
          complete record of the messages that you’ve sent. We encourage you to
          also read our&nbsp;
          <$UserAgreementLink />
          &nbsp;and&nbsp;
          <$PrivacyPolicyLink />
          &nbsp;for more information.
        </$Paragraph>
        <$Paragraph>
          If you have any questions, please email us at&nbsp;
          <$SupportAtAlongEmailLink />. You can also reach out to a teacher,
          parent, or guardian for help understanding any of these terms.
        </$Paragraph>
        <$Paragraph>
          Thank you for taking the time to review our guidelines, and for
          helping to keep Along a safe space!
        </$Paragraph>
      </$TextContainer>
    </PolicyLayout>
  );
}
